import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Users,
  Calendar,
  Wallet,
  HeartHandshake,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Building2,
  Activity,
  DollarSign,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { currencySymbols } from '@/types';
import { format } from 'date-fns';

interface DashboardStats {
  totalMembers: number;
  activeMembers: number;
  totalMeetings: number;
  totalDuesCollected: number;
  totalDonations: number;
  totalExpenses: number;
  attendanceRate: number;
  recentMeetings: {
    id: string;
    meeting_date: string;
    meeting_type: string;
    attendance_count?: number;
    opening_cash?: number;
  }[];
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { currentOrganization, userRole, user } = useAppStore();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const currencySymbol = currentOrganization?.currency
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';

  const netBalance =
    (stats?.totalDuesCollected || 0) +
    (stats?.totalDonations || 0) -
    (stats?.totalExpenses || 0);

  useEffect(() => {
    if (userRole === 'member') {
      navigate('/my-record', { replace: true });
    }
  }, [navigate, userRole]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!currentOrganization || userRole === 'member') return;
      setIsLoading(true);

      try {
        const [
          { count: totalMembers },
          { count: activeMembers },
          { count: totalMeetings },
          { data: recentMeetings },
          { data: duesData },
          { data: donationsData },
          { data: expensesData },
          { data: attendanceData },
        ] = await Promise.all([
          supabase
            .from('members')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', currentOrganization.id),
          supabase
            .from('members')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', currentOrganization.id)
            .eq('status', 'active'),
          supabase
            .from('meetings')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', currentOrganization.id),
          supabase
            .from('meetings')
            .select('id, meeting_date, meeting_type, opening_cash')
            .eq('organization_id', currentOrganization.id)
            .order('meeting_date', { ascending: false })
            .limit(5),
          supabase
            .from('payments')
            .select('amount')
            .eq('organization_id', currentOrganization.id)
            .eq('payment_type', 'dues'),
          supabase
            .from('donations')
            .select('amount, donation_events!inner(organization_id)')
            .eq('donation_events.organization_id', currentOrganization.id),
          supabase
            .from('expenses')
            .select('amount')
            .eq('organization_id', currentOrganization.id),
          supabase
            .from('attendance')
            .select('is_present, meeting:meetings!inner(organization_id)')
            .eq('meeting.organization_id', currentOrganization.id),
        ]);

        const totalDuesCollected = duesData?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
        const totalDonations = donationsData?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0;
        const totalExpenses = expensesData?.reduce((sum, e) => sum + (e.amount || 0), 0) || 0;
        const totalAttendance = attendanceData?.length || 0;
        const presentCount = attendanceData?.filter((a) => a.is_present).length || 0;
        const attendanceRate =
          totalAttendance > 0 ? Math.round((presentCount / totalAttendance) * 100) : 0;

        setStats({
          totalMembers: totalMembers || 0,
          activeMembers: activeMembers || 0,
          totalMeetings: totalMeetings || 0,
          totalDuesCollected,
          totalDonations,
          totalExpenses,
          attendanceRate,
          recentMeetings: recentMeetings || [],
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
        toast.error(t('errors.generic'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [currentOrganization, t, userRole]);

  if (userRole === 'member') {
    return null;
  }

  if (!currentOrganization) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
          <Building2 className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-semibold mb-2">{t('organization.noOrganizations')}</h2>
        <p className="text-muted-foreground mb-6 max-w-sm">{t('organization.createFirst')}</p>
        <Link to="/organizations/create">
          <Button>{t('organization.create')}</Button>
        </Link>
      </div>
    );
  }

  const displayName = user?.full_name || user?.email || 'there';
  const firstName = typeof displayName === 'string' ? displayName.split(' ')[0] : 'there';

  return (
    <div className="space-y-8 max-w-6xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Hello, {firstName} 👋
          </h1>
          <p className="text-muted-foreground mt-1">
            {currentOrganization.name} — {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <Badge variant="outline" className="self-start sm:self-auto capitalize px-3 py-1 text-sm">
          {userRole || 'member'}
        </Badge>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Members"
          value={isLoading ? null : stats?.totalMembers ?? 0}
          sub={isLoading ? null : `${stats?.activeMembers ?? 0} active`}
          icon={Users}
          iconColor="text-blue-500"
          iconBg="bg-blue-500/10"
          href="/members"
        />
        <StatCard
          title="Meetings Held"
          value={isLoading ? null : stats?.totalMeetings ?? 0}
          sub={isLoading ? null : `${stats?.attendanceRate ?? 0}% avg attendance`}
          icon={Calendar}
          iconColor="text-violet-500"
          iconBg="bg-violet-500/10"
          href="/attendance"
        />
        <StatCard
          title="Dues Collected"
          value={isLoading ? null : `${currencySymbol}${(stats?.totalDuesCollected ?? 0).toLocaleString()}`}
          sub={isLoading ? null : 'All time'}
          icon={Wallet}
          iconColor="text-emerald-500"
          iconBg="bg-emerald-500/10"
          href="/dues"
          trend="up"
        />
        <StatCard
          title="Donations"
          value={isLoading ? null : `${currencySymbol}${(stats?.totalDonations ?? 0).toLocaleString()}`}
          sub={isLoading ? null : 'All time'}
          icon={HeartHandshake}
          iconColor="text-rose-500"
          iconBg="bg-rose-500/10"
          href="/donations"
          trend="up"
        />
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Recent Meetings — takes 2 of 3 columns */}
        <div className="lg:col-span-2">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-muted-foreground" />
                  <CardTitle className="text-base font-semibold">Recent Meetings</CardTitle>
                </div>
                <Link to="/attendance">
                  <Button variant="ghost" size="sm" className="text-xs gap-1">
                    View all <ArrowRight className="w-3 h-3" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <Separator />
            <CardContent className="pt-4">
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-14 w-full rounded-lg" />
                  ))}
                </div>
              ) : stats?.recentMeetings && stats.recentMeetings.length > 0 ? (
                <div className="space-y-2">
                  {stats.recentMeetings.map((meeting) => (
                    <div
                      key={meeting.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Calendar className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium capitalize">
                            {meeting.meeting_type} Meeting
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(meeting.meeting_date), 'MMM dd, yyyy')}
                          </p>
                        </div>
                      </div>
                      {meeting.opening_cash != null && (
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Opening cash</p>
                          <p className="text-sm font-semibold">
                            {currencySymbol}{meeting.opening_cash.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <Calendar className="w-8 h-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No meetings recorded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">

          {/* Financial Overview — admin/treasurer only */}
          {(userRole === 'admin' || userRole === 'treasurer') && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-muted-foreground" />
                  <CardTitle className="text-base font-semibold">Finances</CardTitle>
                </div>
              </CardHeader>
              <Separator />
              <CardContent className="pt-4 space-y-4">
                <FinanceRow
                  label="Income"
                  value={isLoading ? null : (stats?.totalDuesCollected ?? 0) + (stats?.totalDonations ?? 0)}
                  symbol={currencySymbol}
                  color="text-emerald-600"
                />
                <FinanceRow
                  label="Expenses"
                  value={isLoading ? null : stats?.totalExpenses ?? 0}
                  symbol={currencySymbol}
                  color="text-rose-600"
                />
                <Separator />
                <FinanceRow
                  label="Net balance"
                  value={isLoading ? null : netBalance}
                  symbol={currencySymbol}
                  color={netBalance >= 0 ? 'text-emerald-600' : 'text-rose-600'}
                  bold
                />
                <Link to="/finances" className="block">
                  <Button variant="outline" size="sm" className="w-full gap-1">
                    Full report <ArrowRight className="w-3 h-3" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Quick Actions</CardTitle>
            </CardHeader>
            <Separator />
            <CardContent className="pt-4 space-y-2">
              <Link to="/members" className="block">
                <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                  <Users className="w-4 h-4" /> View Members
                </Button>
              </Link>
              <Link to="/attendance" className="block">
                <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                  <Calendar className="w-4 h-4" /> Record Attendance
                </Button>
              </Link>
              <Link to="/dues/collect" className="block">
                <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                  <Wallet className="w-4 h-4" /> Collect Dues
                </Button>
              </Link>
              <Link to="/donations" className="block">
                <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                  <HeartHandshake className="w-4 h-4" /> Donations
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: string | number | null;
  sub: string | null;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  href: string;
  trend?: 'up' | 'down';
}

function StatCard({ title, value, sub, icon: Icon, iconColor, iconBg, href, trend }: StatCardProps) {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg}`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          {trend && (
            <span className={`flex items-center gap-1 text-xs font-medium ${trend === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
              {trend === 'up'
                ? <TrendingUp className="w-3 h-3" />
                : <TrendingDown className="w-3 h-3" />}
            </span>
          )}
        </div>

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{title}</p>
          {value === null ? (
            <Skeleton className="h-7 w-24" />
          ) : (
            <p className="text-2xl font-bold">{value}</p>
          )}
          {sub === null ? (
            <Skeleton className="h-3 w-16" />
          ) : (
            <p className="text-xs text-muted-foreground">{sub}</p>
          )}
        </div>

        <Link
          to={href}
          className="absolute bottom-3 right-4 text-xs text-primary hover:underline flex items-center gap-1"
        >
          View <ArrowRight className="w-3 h-3" />
        </Link>
      </CardContent>
    </Card>
  );
}

interface FinanceRowProps {
  label: string;
  value: number | null;
  symbol: string;
  color: string;
  bold?: boolean;
}

function FinanceRow({ label, value, symbol, color, bold }: FinanceRowProps) {
  return (
    <div className="flex items-center justify-between">
      <p className={`text-sm ${bold ? 'font-semibold' : 'text-muted-foreground'}`}>{label}</p>
      {value === null ? (
        <Skeleton className="h-4 w-20" />
      ) : (
        <p className={`text-sm font-semibold ${color}`}>
          {symbol}{value.toLocaleString()}
        </p>
      )}
    </div>
  );
}
